import type {
	LargeFisheriesResponse,
	ModuleFull,
} from "@features/modules/largeFisheries/types";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";
import { transformModuleResponse, prepareModulePayload } from "./factories/moduleApiUtils";

const includedPropList = [
	"fish_type",
	"gear_type_start",
	"gear_type_w",
	"gear_type_wo",
	"total_catch_yr_start",
	"total_catch_yr_w",
	"total_catch_yr_wo",
	"refrigerant_pc_start",
	"refrigerant_pc_w",
	"refrigerant_pc_wo",
	"ice_preserved_catch_pc_start",
	"ice_preserved_catch_pc_w",
	"ice_preserved_catch_pc_wo",
	"fui_t2_start",
	"fui_t2_w",
	"fui_t2_wo",
	"activity",
	"id",
	"status",
	"refrigerant_thread",
	"total_catch_thread",
	"ice_preserved_catch_thread",
	"gear_type_thread",
];
const includedT2PropList = [
	"energy_ef_co2_t2_start",
	"energy_ef_co2_t2_w",
	"energy_ef_co2_t2_wo",
	"energy_ef_ch4_t2_start",
	"energy_ef_ch4_t2_w",
	"energy_ef_ch4_t2_wo",
	"energy_ef_n2o_t2_start",
	"energy_ef_n2o_t2_w",
	"energy_ef_n2o_t2_wo",
	"tonnes_of_ice_t2_start",
	"tonnes_of_ice_t2_w",
	"tonnes_of_ice_t2_wo",
	"inshore_ice_production_kwh_per_tonne_t2_start",
	"inshore_ice_production_kwh_per_tonne_t2_w",
	"inshore_ice_production_kwh_per_tonne_t2_wo",
	"refrigerant_lost_per_tonne_t2_start",
	"refrigerant_lost_per_tonne_t2_w",
	"refrigerant_lost_per_tonne_t2_wo",
];

// Define constants for the module
const MODULE_TYPE = "LargeFisheries";
const TAG: TagTypes = "LargeFisheries";
const TAG_DEFAULTS: TagTypes = "LargeFisheriesDefaults";
const ENDPOINT = FEModulesObj[FEModules.LargeFisheries].path;

const t1PercentageProps = ["ice_preserved_catch_pc", "refrigerant_pc"];

const transformResponse = (response: LargeFisheriesResponse) => transformModuleResponse<LargeFisheriesResponse, ModuleFull>({
	response,
	includedPropList,
	includedT2PropList,
	percentageProps: t1PercentageProps
})

const largeFisheryApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const publicLargeFisheryApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => prepareModulePayload({
	moduleData: data.module,
	tiertwoData: data.tiertwo,
	percentageProps: t1PercentageProps,
	activityId
})

const mutationApiSlice = createModuleUpdateApi<ModuleFull, LargeFisheriesResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformUpdatePayload,
})

export const {
	useGetLargeFisheriesQuery,
	useGetLargeFisheriesDefaultsQuery
} = largeFisheryApiSlice;

export const {
	useGetLargeFisheriesQuery: usePublicGetLargeFisheriesQuery,
	useGetLargeFisheriesDefaultsQuery: usePublicGetLargeFisheriesDefaultsQuery,
} = publicLargeFisheryApiSlice;

export const {
	useUpdateLargeFisheriesMutation,
} = mutationApiSlice;

export const largeFisheryEndpoints = largeFisheryApiSlice.endpoints;
export const largeFisheryMutationEndpoints = mutationApiSlice.endpoints;
